/**
 * JSON-blob parsing helpers for the admin UI.
 *
 * The backend passes several fields through the Admin API verbatim as JSON strings (e.g.
 * `resources.specifications`, `resources.tags`). These helpers parse them defensively so a
 * malformed blob never blanks a page.
 */

/**
 * Parses a JSON string that the API delivers verbatim. Returns an empty object rather than
 * throwing: a malformed blob must never blank out a diagnostic page — the raw text is still shown
 * separately, which is what an operator falls back to.
 */
export function parseJsonObject(raw: unknown): Record<string, unknown> {
  if (raw === null || raw === undefined) {
    return {};
  }

  // Defensive: the field is a string on the wire, but tolerate an already-parsed object so this
  // keeps working if the API is ever changed to send real JSON.
  if (typeof raw === "object") {
    return raw as Record<string, unknown>;
  }

  if (typeof raw !== "string" || raw.trim() === "") {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * Parses a JSON array string (used for `resources.tags`). Returns the string elements only.
 * Same no-throw contract as {@link parseJsonObject}.
 */
export function parseJsonArray(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === "string");
  }

  if (typeof raw !== "string" || raw.trim() === "") {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === "string")
      : [];
  } catch {
    return [];
  }
}

/** The failure a job recorded on a resource's metadata. */
export interface ResourceLastError {
  message: string;
  /** ISO 8601, UTC. Null when the job recorded no time. */
  at: string | null;
}

/**
 * Reads the failure line a job left on `resources.metadata` — the same sentence the failed
 * resource action carries in `errorMessage`. Null when none is recorded. The VM metadata model
 * writes camelCase keys; the generic shape `TerminateResourceJob` writes for a disk or network is
 * PascalCase, so both are accepted.
 */
export function readLastError(rawMetadata: unknown): ResourceLastError | null {
  const metadata = parseJsonObject(rawMetadata);
  const message = metadata.lastError ?? metadata.LastError;
  if (typeof message !== "string" || message.trim() === "") {
    return null;
  }

  const at = metadata.lastErrorAt ?? metadata.LastErrorAt;
  return { message, at: typeof at === "string" && at.trim() !== "" ? at : null };
}
